import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, IconButton, Box, CircularProgress, Alert,
  List, ListItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import billingApi from '../services/billingApi';

/**
 * DowngradeConfirmDialog — shows what the user will lose before confirming
 * a plan downgrade. Fetches the impact preview from
 * GET /api/subscriptions/:id/downgrade-impact?target_plan=<slug> via the
 * shared billingApi client (401-refresh, REACT_APP_BILLING_API_URL /
 * isProxied resolution) and renders the result in an MUI Dialog using the
 * app's brand palette.
 *
 * Props:
 *   open            boolean
 *   onClose         () => void
 *   onConfirm       () => void
 *   subscriptionId  string
 *   targetPlanSlug  string  — planId slug for the target plan
 */
export default function DowngradeConfirmDialog({
  open,
  onClose,
  onConfirm,
  subscriptionId,
  targetPlanSlug,
}) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !subscriptionId || !targetPlanSlug) return;

    setLoading(true);
    setError(null);
    setImpact(null);

    billingApi
      .get(`/api/subscriptions/${subscriptionId}/downgrade-impact?target_plan=${targetPlanSlug}`)
      .then(data => {
        if (data.success) {
          setImpact(data);
        } else {
          setError(data.error || 'Failed to load downgrade impact');
        }
      })
      .catch(err => setError(err.message || 'Failed to load downgrade impact'))
      .finally(() => setLoading(false));
  }, [open, subscriptionId, targetPlanSlug]);

  const targetName = impact?.target_plan_name || targetPlanSlug;
  const effectiveNow = impact?.effective_immediately !== false;
  const imp = impact?.impact;
  const hasImpact = imp && (
    (imp.features_lost?.length || 0) > 0 ||
    (imp.limits_reduced?.length || 0) > 0 ||
    (imp.options_locked?.length || 0) > 0
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          <Typography variant="h6">Downgrade to {targetName}?</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {imp && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This change takes effect{' '}
              <strong>{effectiveNow ? 'immediately' : 'at the end of your current billing period'}</strong>.
              {hasImpact ? ' You will lose access to:' : ' No features will be affected.'}
            </Typography>

            {imp.features_lost?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Features Removed</Typography>
                <List dense disablePadding>
                  {imp.features_lost.map(f => (
                    <ListItem key={f.key} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CancelIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={f.label}
                        slotProps={{ primary: { variant: 'body2' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {imp.limits_reduced?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Limits Reduced</Typography>
                <List dense disablePadding>
                  {imp.limits_reduced.map(f => (
                    <ListItem key={f.key} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <TrendingDownIcon color="warning" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${f.label}: ${f.current} → ${f.new}`}
                        slotProps={{ primary: { variant: 'body2' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {imp.options_locked?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary">Options Locked</Typography>
                <List dense disablePadding>
                  {imp.options_locked.map(f => (
                    <ListItem key={f.key} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <LockIcon color="action" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${f.label}: ${f.locked.join(', ')}`}
                        slotProps={{ primary: { variant: 'body2' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Your existing data will not be deleted. You can still view it, but creating or
              modifying locked features will require upgrading again.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onConfirm}
          disabled={loading || !!error}
        >
          Confirm Downgrade
        </Button>
      </DialogActions>
    </Dialog>
  );
}